import {
  BottomTabBar,
  BrandMark,
  Button,
  EnableNotificationsPrompt,
  Icon,
  ImpersonationBanner,
  Modal,
  NotificationBell,
  NotificationSettings,
  NotificationsProvider,
  PullToRefresh,
  UpdateBanner,
  scrollTopOrRefresh,
  useRefresh,
} from "@corecrm/ui";
import type { IconName } from "@corecrm/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import {
  returnToMyAccount,
  useCompany,
  useImpersonation,
  useMe,
  useOpenQuestions,
  useSignOut,
  useWaiting,
} from "../api/queries";

interface Section {
  to: string;
  label: string;
  short: string;
  icon: IconName;
  count?: number;
}

/** Which section a path belongs to ("/quotes/abc" is Quotes, "/deliverables/x" is Projects). */
export function sectionOf(pathname: string): string {
  const first = pathname.split("/")[1] ?? "";
  if (first === "deliverables") return "/projects";
  return first ? `/${first}` : "/";
}

/* The signed-in portal: the account's name, section tabs (pills from 900px, a bottom tab bar on
   phones), and the page. Mirrors the prototype's portalShell(). */
export function PortalShell() {
  const me = useMe().data!;
  const waiting = useWaiting();
  const openQuestions = useOpenQuestions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [account, setAccount] = useState(false);
  const [settings, setSettings] = useState(false);
  const active = sectionOf(pathname);
  const detail = pathname.split("/").filter(Boolean).length > 1;
  const qc = useQueryClient();
  const [refreshing, refresh] = useRefresh(() => qc.refetchQueries({ type: "active" }));

  // Not a billing contact (UX-192): no Invoices tab, change requests (scope only) instead of
  // quotes, and hours instead of budget.
  const billing = me.billing_contact;
  const sections: Section[] = [
    { to: "/", label: "Home", short: "Home", icon: "home" },
    billing
      ? { to: "/quotes", label: "Quotes", short: "Quotes", icon: "file", count: waiting.counts.quotes }
      : {
          to: "/quotes",
          label: "Change requests",
          short: "Changes",
          icon: "file",
          count: waiting.counts.quotes,
        },
    {
      to: "/projects",
      label: "Projects",
      short: "Projects",
      icon: "layers",
      count: waiting.counts.projects,
    },
    {
      to: "/questions",
      label: "Open questions",
      short: "Questions",
      icon: "help",
      count: openQuestions.data?.length || undefined,
    },
    billing
      ? { to: "/budget", label: "Budget & hours", short: "Budget", icon: "gauge" }
      : { to: "/budget", label: "Hours", short: "Hours", icon: "gauge" },
    ...(billing
      ? [
          {
            to: "/invoices",
            label: "Invoices",
            short: "Invoices",
            icon: "card" as IconName,
            count: waiting.counts.invoices,
          },
        ]
      : []),
  ];
  const current = sections.find((s) => s.to === active);

  useEffect(() => {
    document.body.classList.toggle("has-back", detail);
    document.title = `${current?.label ?? "Client portal"} · CoreNode client portal`;
    return () => document.body.classList.remove("has-back");
  }, [detail, current]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const initials = me.name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <NotificationsProvider api={api}>
      <ImpersonationNotice />
      <div className="app">
        <div className="maincol">
          <header className="top">
            <button
              type="button"
              className="icon-btn top-back mob-only"
              aria-label="Back"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(active))}
            >
              <Icon name="back" />
            </button>
            <Link to="/" className="top-brand" aria-label="CoreNode client portal home">
              <BrandMark />
              <span className="nm">CoreNode</span> <small>client portal</small>
            </Link>
            <div className="top-title" aria-live="polite">
              {current?.label ?? "Client portal"}
            </div>
            <div className="top-sp" />
            <div className="portal-label">{me.account_name}</div>
            <NotificationBell onSettings={() => setSettings(true)} />
            <button
              type="button"
              className="avatar"
              aria-label="Your account"
              onClick={() => setAccount(true)}
            >
              <span>{initials}</span>
            </button>
          </header>
          <main>
            <div className="wrap">
              <div className="portal-head">
                <div>
                  <div className="eyebrow">Client portal</div>
                  <h1>{me.account_name}</h1>
                </div>
                <div className="muted">Signed in as {me.name}</div>
              </div>
              <nav className="ptabs" aria-label="Portal sections">
                {sections.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className={s.to === active ? "on" : undefined}
                    aria-current={s.to === active ? "page" : undefined}
                    aria-label={s.count ? `${s.label}, ${s.count} waiting` : undefined}
                  >
                    {s.label}
                    {!!s.count && <span className="dot">{s.count}</span>}
                  </Link>
                ))}
              </nav>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <BottomTabBar
        items={sections.map((s) => ({
          key: s.to,
          label: s.short,
          icon: s.icon,
          active: s.to === active,
          badge: s.count,
          // Tapped again while on that page: scroll up, then refresh.
          onSelect: () => (pathname === s.to ? scrollTopOrRefresh(refresh) : navigate(s.to)),
        }))}
      />
      <PullToRefresh refreshing={refreshing} onRefresh={refresh} />
      <UpdateBanner />
      {account && (
        <AccountSheet
          onClose={() => setAccount(false)}
          onSettings={() => {
            setAccount(false);
            setSettings(true);
          }}
        />
      )}
      {settings && (
        <Modal title="Notification settings" onClose={() => setSettings(false)}>
          <NotificationSettings />
        </Modal>
      )}
      <EnableNotificationsPrompt
        appName="the CoreNode client portal"
        message="Get desktop alerts when your project team needs you"
      />
    </NotificationsProvider>
  );
}

/** The caution banner while an administrator is logged in as this client (UX-152). */
function ImpersonationNotice() {
  const { data } = useImpersonation();
  return data ? <ImpersonationBanner who={data} onReturn={returnToMyAccount} /> : null;
}

function AccountSheet({ onClose, onSettings }: { onClose: () => void; onSettings: () => void }) {
  const me = useMe().data!;
  const company = useCompany().data;
  const signOut = useSignOut();
  const navigate = useNavigate();
  const impersonating = !!useImpersonation().data;
  return (
    <Modal
      title="Your account"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            busy={signOut.isPending}
            onClick={() =>
              // Signing out as the client returns the administrator to their own account (UX-152).
              impersonating
                ? returnToMyAccount()
                : signOut.mutate(undefined, { onSettled: () => navigate("/sign-in", { replace: true }) })
            }
          >
            {impersonating ? "Return to my account" : "Sign out"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div>
          <strong>{me.name}</strong>
          <div className="muted">{me.email}</div>
          <div className="faint">{me.account_name}</div>
        </div>
        <p className="muted">
          {me.sign_in_method === "password"
            ? "You sign in with a password. "
            : "You sign in with a one-time link we email you. "}
          <Link to="/forgot-password" onClick={onClose}>
            {me.sign_in_method === "password" ? "Change your password" : "Set a password instead"}
          </Link>
        </p>
        <p>
          <button type="button" className="link-btn" onClick={onSettings}>
            Notification settings
          </button>
        </p>
        {me.mcp_enabled && (
          <p>
            <Link to="/claude" onClick={onClose}>
              Use with Claude
            </Link>
            <span className="faint"> · connect Claude to this portal</span>
          </p>
        )}
        <p className="faint">
          Questions about your project or an invoice? Reply to any email from CoreNode
          {company?.email ? (
            <>
              , or write to <a href={`mailto:${company.email}`}>{company.email}</a>
            </>
          ) : null}
          .
        </p>
      </div>
    </Modal>
  );
}
